/**
 * A proposal — what one-shot generation answers with. Story 27.3, RULE-019, RULE-020.
 *
 * PRD §3.5: "Describe business and ideal customer → complete draft funnel as a reviewable
 * diff on an empty canvas." A proposal is two things and nothing else (DECISION-167): an
 * ARCHETYPE the model chose from the funnel's five — shown in the review with what it means,
 * and fixed only when a person accepts — and a sequence of OPERATIONS in RULE-019's
 * vocabulary. It is never a funnel the model wrote: "the AI never generates UI or free-form
 * output. It emits operations against the funnel schema", and the server applies them.
 *
 * THE LAST OPERATION IS THE PERSON'S (DECISION-180, Story 27.6): the route ends every proposal
 * with a `set_icp_profile` carrying the description they typed, composed by the server, never by
 * the model — so the review can show it as their words, and accepting it is one operation more.
 *
 * ONE DEFINITION, TWO READERS. `proposalSchema` is what `jsonSchemaOf` hands the model and
 * what the answer is held to, so the prompt and the parser cannot disagree (PRD §3.2).
 *
 * WRITES NOTHING (RULE-020): "AI turns write to a pending-diff state, never to
 * `funnels.schema`." A proposal is a value returned to the person who asked; Story 27.4's
 * accept is what applies it, through `edit()`, one operation at a time.
 */
import { z } from 'zod'
import { archetypeSchema } from './archetype'
import { MAX_OPERATIONS, operationSchema } from './operations'

/**
 * The longest description one-shot takes. "A sentence or two" (the story's own words) about a
 * business and who it wants — a long paragraph fits; a pasted document is a 400, because it is
 * input the model reads at the plan's expense and, past a point, a way to use it as something
 * else (PRD §5.7's LLM-proxy threat).
 */
export const DESCRIPTION_MAX = 2000

/** The shortest: fewer words than this describe nobody a funnel could be built for. */
export const DESCRIPTION_MIN = 20

/** A proposal, as the model is asked for one and as its answer is held. */
export const proposalSchema = z
  .object({
    archetype: archetypeSchema.describe(
      'Which of the five kinds of funnel this is — scorecard, qualifier, recommender, triage or eligibility.',
    ),
    operations: z
      .array(operationSchema)
      .min(1)
      .max(MAX_OPERATIONS)
      .describe('The funnel, built from nothing by these operations, in order.'),
  })
  .strict()
export type Proposal = z.infer<typeof proposalSchema>

/**
 * The same shape with each operation left unread — for the answer's first pass. The operations
 * are read one by one by `applyOperations`, whose refusal names the operation and why; a Zod
 * error over a nine-way union would name neither.
 */
export const proposalEnvelopeSchema = z
  .object({
    archetype: archetypeSchema,
    operations: z.array(z.unknown()).min(1).max(MAX_OPERATIONS),
  })
  .strict()

/**
 * A description, trimmed and checked — or why not, in words that say what to write.
 * Whitespace runs are folded so a description and the same description re-typed are one cache
 * entry, not two.
 */
export function readDescription(input: unknown): { ok: true; description: string } | { ok: false; reason: string } {
  if (typeof input !== 'string') {
    return { ok: false, reason: 'Describe your business and your ideal customer in a sentence or two.' }
  }
  const description = input.replace(/\s+/g, ' ').trim()
  if (description.length < DESCRIPTION_MIN) {
    return {
      ok: false,
      reason: 'Tell us a little more — what your business does, and who your ideal customer is, in a sentence or two.',
    }
  }
  if (description.length > DESCRIPTION_MAX) {
    return {
      ok: false,
      reason: `Keep the description under ${DESCRIPTION_MAX.toLocaleString('en-US')} characters — a sentence or two about the business and who it wants is enough.`,
    }
  }
  return { ok: true, description }
}
